import { Button } from '@progress/kendo-react-buttons';
import type { PaddingOptions } from 'maplibre-gl';
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { OnMapControlsDisplayEnum } from '../../../../common/enums';
import GdfViewer from '../../../../components/gdf/GdfViewer';
import { PitchRollControl } from '../../../../components/map-interaction/PitchRollControl';
import { TerrainControl } from '../../../../components/map-interaction/TerrainControl';
import { RouteAnalyzer } from '../../../../components/route-analyzer/RouteAnalyzer';
import WeatherAltitude from '../../../../components/weather/WeatherAltitude';
import WeatherFlyout from '../../../../components/weather/WeatherFlyout';
import WeatherList from '../../../../components/weather/WeatherList';
import WeatherTimeline from '../../../../components/weather/WeatherTimeline';
import useDebugFeatures from '../../../../hooks/use-debug-features';
import {
    AirportTowerOutline,
    AttachImageOutline,
    BorderColorOutline,
    DeltaOutline,
    FilterOutline,
    InfoCircleOutline,
    LayersOutline,
    SearchOutline,
    ThunderstormOutline
} from '../../../../icons/icons';
import { NoteCategory } from '../../../../types/notes';
import { WorkspaceLayout } from '../../../../types/settings';
import { FlightProfile } from '../../../components/flight-profile/FlightProfile';
import { PlaybackControls } from '../../../components/playback/PlaybackControls';
import { EventBus, useEvent } from '../../../contexts/event-bus';
import { MapPaddingContext } from '../../../contexts/map-padding-context';
import { WorkspaceItemContext } from '../../../contexts/workspace/workspace-item-context';
import { WorkspaceManagementContext } from '../../../contexts/workspace/workspace-management-context';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hooks';
import { useSlideShow } from '../../../hooks/use-slideshow';
import { hasTerrainOptionSelector } from '../../../state/selectors';
import { toggleLeftFlyout } from '../../../state/slices/workspace-slice';
import { FlightForwardControls } from '../components/FlightForwardControls';
import { TimezoneBar } from '../components/TimeZoneBar';
import FlightDetails from '../flight-details/FlightDetails';
import PinnedFlights from '../flight-details/PinnedFlights';
import AirportSummary from '../flyouts/AirportSummary';
import EmergencyDiversion from '../flyouts/EmergencyDiversion';
import FiltersFlyout from '../flyouts/filters/FiltersFlyout';
import Flyout from '../flyouts/Flyout';
import { LayersFlyout } from '../flyouts/LayersFlyout/LayersFlyout';
import Notes from '../flyouts/Notes';
import SearchFlyout from '../flyouts/SearchFlyout/SearchFlyout';
import { HomeFlyoutTypes } from '../flyouts/types';
import UdViewsFlyout from '../flyouts/views/UdViewsFlyout';
import { useHoverVisible } from '../hooks/use-hover-visible';
import { useFocusedWorkspaceSelector, useWorkspaceView } from '../hooks/use-view';
import { AirportMonitorStatusUpdates } from '../layers/AirportMonitorStatusUpdates';
import { FlightMapUpdates } from '../layers/flight/FlightMapUpdates';
import { HomePanel } from '../panels/HomePanel';
import { Watermark } from './Watermark';
import { WorkspaceItem } from './WorkspaceItem';

export default function Workspaces() {
    const dispatch = useAppDispatch();
    const context = React.useContext(WorkspaceManagementContext);
    const items = useAppSelector((x) => x.workspace.items);
    const focusedWorkspaceId = useAppSelector((state) => state.workspace.selectedId);
    const [showFlyout, setShowFlyout] = React.useState<HomeFlyoutTypes | null>(null);
    const leftPanelRef = useRef<HTMLDivElement>(null);
    const leftFlyoutRef = useRef<HTMLDivElement>(null);
    const rightRef = useRef<HTMLDivElement>(null);
    const [mapPadding, setMapPadding] = useState<PaddingOptions>({ top: 10, right: 10, bottom: 10, left: 10 });
    const leftFlyout = useFocusedWorkspaceSelector((x) => x?.leftFlyout);
    const hasFlightDetails = useAppSelector((state) => state.workspace.items[state.workspace.selectedId]?.flights.selected.length > 0);
    const debugFeatures = useDebugFeatures();
    const workspaceLayout = useAppSelector((x) => x.workspace.layout);
    const isGlobe = useAppSelector((state) => state.workspace.items[state.workspace.selectedId]?.view.map.useGlobeView);
    const hasTerrain = useAppSelector(hasTerrainOptionSelector);
    const activeFilterCount = useWorkspaceView((x) => x.plane.filter.planeFilterSettings.filter((x) => x.isActive).length);
    const filtersEnabled = useWorkspaceView((x) => x.plane.filter.useFilters);
    const tutorial = useAppSelector((x) => x.global.tutorial);
    const onMapControlsDisplaySettings = useAppSelector((x) => x.settings.OnMapControlsDisplay);
    const showRouteAnalyzer = useAppSelector((x) => x.workspace.panels.routeAnalyzer);
    const showFlightProfile = useFocusedWorkspaceSelector((x) => x.flights.settings.showProfile && x.flights.selected.length > 0);
    const leftHover = useHoverVisible();
    const rightHover = useHoverVisible();
    const bottomHover = useHoverVisible();

    const makeControlStyle = (visible: boolean): CSSProperties => ({
        opacity:
            onMapControlsDisplaySettings === OnMapControlsDisplayEnum.Hide
                ? 0
                : onMapControlsDisplaySettings === OnMapControlsDisplayEnum.OnlyWhenActive
                  ? visible
                      ? 1
                      : 0
                  : 1,
        transition: 'opacity 0.3s ease'
    });

    useSlideShow();

    useEvent(
        'open-weather-settings',
        useCallback(
            (args) => {
                // If we were on a different flyout
                if (showFlyout !== HomeFlyoutTypes.layers) {
                    // Change it
                    setShowFlyout(HomeFlyoutTypes.layers);
                    // And re-fire the event so that layers can handle it
                    self.setTimeout(() => {
                        EventBus.fire('open-weather-settings', args);
                    }, 10);
                }
            },
            [showFlyout]
        )
    );

    useEvent(
        'add-new-filter',
        useCallback(
            (args) => {
                if (showFlyout !== HomeFlyoutTypes.filters) {
                    setShowFlyout(HomeFlyoutTypes.filters);
                    self.setTimeout(() => {
                        EventBus.fire('add-new-filter', args);
                    }, 10);
                }
            },
            [showFlyout]
        )
    );

    useEvent(
        'open-flyout',
        useCallback(
            (args) => {
                if (args.type !== showFlyout) {
                    setShowFlyout(args.type);
                    // Re-fire
                    self.setTimeout(() => {
                        EventBus.fire('open-flyout', args);
                    }, 10);
                }
            },
            [showFlyout]
        )
    );

    useEffect(() => {
        if (tutorial) {
            setShowFlyout(null);
        }
    }, [tutorial]);

    useEffect(() => {
        const update = () => {
            const leftInner = leftPanelRef.current?.offsetWidth ?? 0;
            const visibleFlyout = leftFlyoutRef.current
                ? (Array.from(leftFlyoutRef.current.children) as HTMLElement[]).find((c) => c.offsetWidth > 0)
                : null;
            const largeFlyout = visibleFlyout?.offsetWidth ?? 0;
            const right = rightRef.current?.offsetWidth ?? 0;
            setMapPadding({ top: 10, right: right + 10, bottom: 10, left: leftInner + largeFlyout + 10 });
        };
        const observer = new ResizeObserver(update);
        [leftPanelRef, leftFlyoutRef, rightRef].forEach((r) => r.current && observer.observe(r.current));
        update();
        return () => observer.disconnect();
    }, [leftFlyout]);

    return (
        <MapPaddingContext.Provider value={mapPadding}>
            {debugFeatures && <GdfViewer />}

            <div className="d-flex flex-column h-100 overflow-hidden">
                <TimezoneBar />
                <div className="flex-grow-1 d-flex">
                    <div
                        className="flex-grow-1 position-relative"
                        style={{
                            minHeight: 0,
                            display: 'grid',
                            gridGap: '1px',
                            ...getGridStyles(workspaceLayout)
                        }}>
                        {items.map(
                            (x, i) =>
                                context.items[x.id] && (
                                    <div
                                        key={x.id}
                                        className="w-100 h-100"
                                        style={{
                                            minHeight: 0,
                                            minWidth: 0,
                                            ...((workspaceLayout === WorkspaceLayout.triVertical &&
                                                i === 0 && {
                                                    gridRow: '1 / 3',
                                                    gridColumn: '1'
                                                }) ||
                                                (workspaceLayout === WorkspaceLayout.triHorizontal &&
                                                    i === 0 && {
                                                        gridRow: '1',
                                                        gridColumn: '1 / 3'
                                                    }))
                                        }}>
                                        {/* This context is scoped to a particular workspace map */}
                                        <WorkspaceItemContext.Provider value={context.items[x.id]}>
                                            <WorkspaceItem id={x.id} />
                                        </WorkspaceItemContext.Provider>
                                    </div>
                                )
                        )}

                        <div
                            id="workspace-maps-overlay"
                            className="p-2 pe-none d-flex flex-column gap-2"
                            style={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                left: 0,
                                bottom: 0
                            }}>
                            <div className="flex-grow-1 min-h-0 d-flex gap-2">
                                <Watermark />

                                {/* Left side fly outs */}
                                <div
                                    className="d-flex gap-2 flex-grow-1"
                                    style={makeControlStyle(leftHover.visible)}
                                    onMouseEnter={leftHover.onMouseEnter}
                                    onMouseLeave={leftHover.onMouseLeave}>
                                    {/* 1st column */}
                                    <div
                                        ref={leftPanelRef}
                                        className="min-h-0 position-relative"
                                        style={{
                                            display: 'grid',
                                            gridAutoRows: 'minmax(0, fit-content)',
                                            rowGap: '0.5rem',
                                            alignContent: 'start',
                                            justifyItems: 'start'
                                        }}>
                                        {/* Don't move this control to the div below, because it has overflow-hidden and the popup will not appear because of it */}
                                        <div className={'d-flex gap-2'} style={{ position: 'absolute', top: 0, left: 0 }}>
                                            {!isGlobe && <PitchRollControl />}
                                            {hasTerrain && <TerrainControl />}
                                        </div>
                                        <div className={'d-flex gap-2 max-h-100 overflow-hidden'}>
                                            {!isGlobe && <div style={{ width: 45 }}></div>}
                                            {hasTerrain && <div style={{ width: 29 }}></div>}
                                            <WeatherList />
                                        </div>

                                        <PinnedFlights />

                                        {hasFlightDetails && (
                                            <Flyout
                                                id="flight-details-flyout"
                                                dir="l"
                                                show={hasFlightDetails}
                                                icon={InfoCircleOutline}
                                                onClose={() => {
                                                    context.items[focusedWorkspaceId].flightLayerFacade.selection.deselectAll();
                                                }}>
                                                <FlightDetails />
                                            </Flyout>
                                        )}
                                    </div>

                                    {/* 2nd column */}
                                    <div ref={leftFlyoutRef} className="flex-grow-1 d-flex flex-sm-column flex-xl-row gap-2">
                                        <Flyout
                                            className="flex-grow-1"
                                            show={leftFlyout?.type === 'airport'}
                                            title="Airport summary"
                                            style={{ maxWidth: 1040 }}
                                            icon={AirportTowerOutline}
                                            dir="l"
                                            onClose={() => dispatch(toggleLeftFlyout())}>
                                            <AirportSummary id={leftFlyout?.type === 'airport' ? leftFlyout.airportId : null} />
                                        </Flyout>

                                        <Flyout
                                            className="flex-grow-1"
                                            show={leftFlyout?.type === 'diversion'}
                                            title="Emergency diversion"
                                            style={{ maxWidth: 700, height: 'fit-content' }}
                                            dir="l"
                                            icon={DeltaOutline}
                                            onClose={() => {
                                                dispatch(toggleLeftFlyout());
                                            }}>
                                            <EmergencyDiversion flightId={leftFlyout?.type === 'diversion' ? leftFlyout.flightId : null} />
                                        </Flyout>

                                        <Flyout
                                            className="flex-grow-1"
                                            show={leftFlyout?.type === 'notes'}
                                            title={`${leftFlyout?.type === 'notes' ? leftFlyout.airportId : null} Notes`}
                                            icon={BorderColorOutline}
                                            onClose={() => {
                                                dispatch(toggleLeftFlyout());
                                            }}
                                            style={{ maxWidth: 700, height: 'fit-content' }}
                                            dir={'l'}>
                                            <Notes
                                                itemId={leftFlyout?.type === 'notes' ? leftFlyout.airportId : null}
                                                category={NoteCategory.airport}></Notes>
                                        </Flyout>
                                    </div>
                                </div>

                                {/* Right side fly outs  */}
                                <div
                                    ref={rightRef}
                                    className="d-flex gap-2 z-2 align-items-start h-100"
                                    style={makeControlStyle(rightHover.visible)}
                                    onMouseEnter={rightHover.onMouseEnter}
                                    onMouseLeave={rightHover.onMouseLeave}>
                                    <div
                                        className="position-relative h-100"
                                        style={{ display: 'grid', gridTemplateColumns: 'max-content', gridTemplateRows: '100%' }}>
                                        <div style={{ gridRow: '1', gridColumn: '1' }}>
                                            <Flyout dir="r" show={showFlyout === 0} onClose={() => setShowFlyout(null)}>
                                                <SearchFlyout />
                                            </Flyout>
                                        </div>

                                        <div style={{ gridRow: '1', gridColumn: '1' }}>
                                            <Flyout
                                                dir="r"
                                                title={'Layers'}
                                                icon={LayersOutline}
                                                show={showFlyout === 2}
                                                onClose={() => setShowFlyout(null)}>
                                                <LayersFlyout />
                                            </Flyout>
                                        </div>

                                        <div style={{ gridRow: '1', gridColumn: '1' }}>
                                            <Flyout
                                                dir="r"
                                                show={showFlyout === 1}
                                                title="Views"
                                                icon={AttachImageOutline}
                                                onClose={() => setShowFlyout(null)}>
                                                <UdViewsFlyout />
                                            </Flyout>
                                        </div>

                                        <div style={{ gridRow: '1', gridColumn: '1' }}>
                                            <Flyout
                                                dir="r"
                                                show={showFlyout === 3}
                                                title="Weather"
                                                icon={ThunderstormOutline}
                                                onClose={() => setShowFlyout(null)}>
                                                <WeatherFlyout />
                                            </Flyout>
                                        </div>

                                        <div style={{ gridRow: '1', gridColumn: '1' }}>
                                            <Flyout
                                                dir="r"
                                                show={showFlyout === 4}
                                                title="Aircraft filters"
                                                icon={FilterOutline}
                                                onClose={() => setShowFlyout(null)}>
                                                <FiltersFlyout />
                                            </Flyout>
                                        </div>
                                    </div>

                                    {/* Tools Buttons */}
                                    <div className="d-flex flex-column pe-auto gap-2 h-100" style={{ zIndex: 101 }}>
                                        <Button
                                            id="search-btn"
                                            title="Search"
                                            data-tooltip-left
                                            svgIcon={SearchOutline}
                                            size={'medium'}
                                            style={{
                                                color: showFlyout === HomeFlyoutTypes.search ? 'var(--info-blue)' : undefined,
                                                aspectRatio: '1/1'
                                            }}
                                            onClick={() =>
                                                setShowFlyout(showFlyout === HomeFlyoutTypes.search ? null : HomeFlyoutTypes.search)
                                            }></Button>
                                        <Button
                                            id="views-btn"
                                            title="Views"
                                            data-tooltip-left
                                            svgIcon={AttachImageOutline}
                                            size={'medium'}
                                            style={{
                                                color: showFlyout === HomeFlyoutTypes.views ? 'var(--info-blue)' : undefined,
                                                aspectRatio: '1/1'
                                            }}
                                            onClick={() =>
                                                setShowFlyout(showFlyout === HomeFlyoutTypes.views ? null : HomeFlyoutTypes.views)
                                            }></Button>
                                        <div className="vertical-button-group position-relative" id="map-vlwa-nav">
                                            <Button
                                                id="layers-btn"
                                                title="Layers"
                                                data-tooltip-left
                                                svgIcon={LayersOutline}
                                                size={'medium'}
                                                style={{
                                                    color: showFlyout === HomeFlyoutTypes.layers ? 'var(--info-blue)' : undefined,
                                                    aspectRatio: '1/1'
                                                }}
                                                onClick={() =>
                                                    setShowFlyout(showFlyout === HomeFlyoutTypes.layers ? null : HomeFlyoutTypes.layers)
                                                }></Button>
                                            <Button
                                                id="weather-btn"
                                                title="Weather"
                                                data-tooltip-left
                                                svgIcon={ThunderstormOutline}
                                                size={'medium'}
                                                style={{
                                                    color: showFlyout === HomeFlyoutTypes.weather ? 'var(--info-blue)' : undefined,
                                                    aspectRatio: '1/1'
                                                }}
                                                onClick={() =>
                                                    setShowFlyout(showFlyout === HomeFlyoutTypes.weather ? null : HomeFlyoutTypes.weather)
                                                }></Button>
                                            <Button
                                                id="filter-btn"
                                                title="Aircraft filters"
                                                data-tooltip-left
                                                svgIcon={FilterOutline}
                                                size={'medium'}
                                                style={{
                                                    color: showFlyout === HomeFlyoutTypes.filters ? 'var(--info-blue)' : undefined,
                                                    aspectRatio: '1/1'
                                                }}
                                                className="position-relative"
                                                onClick={() =>
                                                    setShowFlyout(showFlyout === HomeFlyoutTypes.filters ? null : HomeFlyoutTypes.filters)
                                                }></Button>

                                            {filtersEnabled && (
                                                <div
                                                    className="d-flex align-items-center justify-content-center"
                                                    style={{
                                                        position: 'absolute',
                                                        bottom: 4,
                                                        right: 4,
                                                        backgroundColor: 'var(--kendo-color-primary)',
                                                        color: 'white',
                                                        fontSize: 10,
                                                        aspectRatio: '1/1',
                                                        height: 11,
                                                        textAlign: 'center',
                                                        lineHeight: 1,
                                                        borderRadius: '50%'
                                                    }}>
                                                    {activeFilterCount}
                                                </div>
                                            )}
                                        </div>
                                        <WeatherAltitude />
                                    </div>
                                </div>
                            </div>

                            {/* Bottom  */}
                            <div
                                className="pe-auto d-flex flex-column gap-2"
                                style={makeControlStyle(bottomHover.visible)}
                                onMouseEnter={bottomHover.onMouseEnter}
                                onMouseLeave={bottomHover.onMouseLeave}>
                                <FlightForwardControls />
                                <WeatherTimeline />
                                <PlaybackControls />
                            </div>
                        </div>
                    </div>
                </div>
                {/* TODO: currently route analyzer component needs to be attached to the react tree because it contains event listeners that need to handle messages even if the route analyzer is hidden
                          move the logic in those listeners to redux
                */}
                <HomePanel
                    title="Route analyzer"
                    style={{ height: showRouteAnalyzer ? 'max(20%, 100px)' : 0 }}
                    className={showRouteAnalyzer ? 'border-top' : undefined}>
                    <RouteAnalyzer />
                </HomePanel>
                {showFlightProfile && (
                    <HomePanel title="Flight profile" style={{ height: 'max(24%, 200px)' }} className="border-top">
                        <FlightProfile />
                    </HomePanel>
                )}
                <FlightMapUpdates />
                <AirportMonitorStatusUpdates />
            </div>
        </MapPaddingContext.Provider>
    );
}

function getGridStyles(layout: WorkspaceLayout): { gridTemplate: string } {
    switch (layout) {
        case WorkspaceLayout.single:
            return { gridTemplate: '1fr / 1fr' };
        case WorkspaceLayout.dualVertical:
            return { gridTemplate: '1fr / 1fr 1fr' };
        case WorkspaceLayout.dualHorizontal:
            return { gridTemplate: '1fr 1fr / 1fr' };
        case WorkspaceLayout.triVertical:
            return { gridTemplate: '1fr 1fr / 1fr 1fr' };
        case WorkspaceLayout.triHorizontal:
            return { gridTemplate: '1fr 1fr / 1fr 1fr' };
        case WorkspaceLayout.quad:
            return { gridTemplate: '1fr 1fr / 1fr 1fr' };
    }
}
